// App.js
import { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { auth, db, ADMIN_EMAIL } from "./firebase";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { 
  fetchQuestions, 
  fetchInterviews, 
  fetchPositions, 
  LARK_TABLES,
  createRecord,
  updateRecord,
  deleteRecord,
  discoverTables,
  discoverBase
} from "./larkService_v2";
import { getDoc, doc } from "firebase/firestore";

import Login from "./Login";
import AvatarUpload from "./AvatarUpload";
import CandidateInterview from "./CandidateInterview";
import AdminDashboard from "./AdminDashboard";
import InterviewDetail from "./InterviewDetail";
import "./App.css";

// ─── Shared Sidebar Layout ───
function AdminLayout({ user, onLogout, children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [avatarUrl, setAvatarUrl] = useState(null);

  const isActive = (path) => location.pathname === path;

  return (
    <div className="dashboard">
      <aside className="sidebar">
        <h2>🧠 AI Hiring</h2>
        <ul>
          <li className={isActive("/dashboard") ? "active" : ""} onClick={() => navigate("/dashboard")}>📊 Dashboard</li>
          <li className={isActive("/positions") ? "active" : ""} onClick={() => navigate("/positions")}>💼 Positions</li>
          <li className={isActive("/admin") ? "active" : ""} onClick={() => navigate("/admin")}>👥 Candidates</li>
          <li className={isActive("/settings") ? "active" : ""} onClick={() => navigate("/settings")}>⚙️ Settings</li>
          <li className={isActive("/interview") ? "active" : ""} onClick={() => navigate("/interview")}>🎯 Test Interview</li>
        </ul>
      </aside>

      <main className="main-content">
        <header className="topbar">
          <h1>
            {isActive("/dashboard") && "Admin Dashboard"}
            {isActive("/positions") && "Position Management"}
            {isActive("/settings") && "Settings — Questions"}
            {isActive("/admin") && "Candidates"}
            {isActive("/interview") && "Test Interview"}
          </h1>
          <div className="user-info">
            {avatarUrl ? (
              <img src={avatarUrl} alt="HR Avatar" className="hr-avatar" />
            ) : (
              <div className="default-avatar">HR</div>
            )}
            <span>{user.email}</span>
            <button className="logout-btn" onClick={async () => await onLogout()}>
              Logout
            </button>
          </div>
        </header>
        {children}
      </main>
    </div>
  );
}

// ─────────────────── Dashboard Page (Clean & Short) ───────────────────
function DashboardPage({ user }) {
  const navigate = useNavigate();

  const [questions, setQuestions] = useState([]);
  const [interviewRecords, setInterviewRecords] = useState([]);
  const [positions, setPositions] = useState([]);
  const [loadingInterviews, setLoadingInterviews] = useState(true);
  const [larkError, setLarkError] = useState("");

  useEffect(() => {
    const loadData = async () => {
      try {
        const [qs, ivs, pos] = await Promise.all([
          fetchQuestions(),
          fetchInterviews(),
          fetchPositions()
        ]);
        setQuestions(qs);
        // Sort interviews: newest first (by completedAt, then by record ID)
        const sorted = [...ivs].sort((a, b) => {
          const timeA = a.completedAt ? new Date(a.completedAt).getTime() : 0;
          const timeB = b.completedAt ? new Date(b.completedAt).getTime() : 0;
          if (timeA !== timeB) return timeB - timeA;
          return (b.id || "").localeCompare(a.id || "");
        });
        setInterviewRecords(sorted);
        setPositions(pos);
        setLoadingInterviews(false);
      } catch (err) {
        console.error("Dashboard Load Error:", err);
        setLarkError(err.message || JSON.stringify(err));
        setLoadingInterviews(false);
      }
    };
    loadData();
    const interval = setInterval(loadData, 10000); 
    return () => clearInterval(interval);
  }, []);

  const totalQuestions = questions.length;
  const totalInterviews = interviewRecords.length;
  const totalPositions = positions.length;
  const getScoreColor = (score) => score >= 7 ? "#10b981" : score >= 4 ? "#f59e0b" : "#ef4444";

  return (
    <>
      {/* Quick Action Cards */}
      <div className="quick-actions">
        <button className="action-card" onClick={() => navigate("/positions")}>
          <span className="action-icon">💼</span>
          <span className="action-label">Manage Positions</span>
          <span className="action-desc">Add roles & job descriptions for AI rating</span>
        </button>
        <button className="action-card" onClick={() => navigate("/admin")}>
          <span className="action-icon">👥</span>
          <span className="action-label">View Candidates</span>
          <span className="action-desc">Review all candidate results</span>
        </button>
        <button className="action-card" onClick={() => navigate("/settings")}>
          <span className="action-icon">⚙️</span>
          <span className="action-label">Questions & Settings</span>
          <span className="action-desc">Manage interview questions</span>
        </button>
      </div>

      {/* Stats Cards */}
      <div className="stats-cards">
        <div className="card stat-card">
          <h3>Total Questions</h3>
          <p>{totalQuestions}</p>
        </div>
        <div className="card stat-card">
          <h3>Total Interviews</h3>
          <p>{totalInterviews}</p>
        </div>
        <div className="card stat-card">
          <h3>Positions</h3>
          <p>{totalPositions}</p>
        </div>
      </div>

      {larkError && (
        <div style={{ background: "#fee2e2", color: "#b91c1c", padding: "16px", borderRadius: "8px", margin: "20px 0", border: "1px solid #f87171" }}>
          <strong>Lark Connection Error:</strong> {larkError}
        </div>
      )}

      {/* Recent Interviews Table */}
      <div className="card table-card" style={{ marginTop: 20 }}>
        <h3>Recent Interviews</h3>
        {loadingInterviews ? (
          <p>Loading interview records...</p>
        ) : interviewRecords.length === 0 ? (
          <p>No interview records found.</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Candidate</th>
                <th>Position</th>
                <th>Status</th>
                <th>Resume Score</th>
                <th>Interview Score</th>
                <th>Final Score</th>
                <th>Date</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              {interviewRecords.slice(0, 10).map((r) => (
                <tr key={r.id}>
                  <td>{r.candidateName || (r.userId?.length > 25 ? r.userId.slice(0, 25) + "..." : r.userId)}</td>
                  <td>
                    {r.positionTitle ? (
                      <span style={{ padding: "3px 8px", borderRadius: "6px", background: "#ede9fe", color: "#6d28d9", fontSize: "0.75rem", fontWeight: 600 }}>
                        {r.positionTitle}
                      </span>
                    ) : (
                      <span style={{ color: "#94a3b8", fontSize: "0.75rem" }}>—</span>
                    )}
                  </td>
                  <td>
                    <span style={{
                      padding: "4px 8px", borderRadius: "12px", fontSize: "0.75rem", fontWeight: "600",
                      backgroundColor: (r.status === "completed" || !r.status) ? "#d1fae5" : (r.status === "active" ? "#fef3c7" : "#e0e7ff"),
                      color: (r.status === "completed" || !r.status) ? "#065f46" : (r.status === "active" ? "#92400e" : "#4338ca"),
                    }}>
                      {r.status === "completed" || !r.status ? "Done" : (r.status === "active" ? "Live" : "Pending")}
                    </span>
                  </td>
                  <td><span style={{ color: getScoreColor(r.resumeScore || 0), fontWeight: "bold" }}>{r.resumeScore || 0}/10</span></td>
                  <td><span style={{ color: getScoreColor(r.interviewScore || 0), fontWeight: "bold" }}>{r.interviewScore || 0}/10</span></td>
                  <td><span style={{ color: getScoreColor(r.finalScore || 0), fontWeight: "bold" }}>{r.finalScore || 0}/10</span></td>
                  <td>{r.completedAt ? new Date(r.completedAt).toLocaleDateString() : "N/A"}</td>
                  <td><button onClick={() => navigate(`/interview-detail/${r.id}`)}>View →</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}

// ─────────────────── Positions Page ───────────────────
function PositionsPage() {
  const navigate = useNavigate();

  const [positions, setPositions] = useState([]);
  const [loadingPositions, setLoadingPositions] = useState(true);
  const [newPositionTitle, setNewPositionTitle] = useState("");
  const [newJobDescription, setNewJobDescription] = useState("");
  const [editPositionId, setEditPositionId] = useState(null);
  const [editPositionTitle, setEditPositionTitle] = useState("");
  const [editJobDescription, setEditJobDescription] = useState("");
  
  // New States for Questions
  const [isAddingQuestions, setIsAddingQuestions] = useState(false);
  const [newQuestions, setNewQuestions] = useState(Array(10).fill(""));
  const [viewingQuestionsFor, setViewingQuestionsFor] = useState(null);
  const [positionQuestions, setPositionQuestions] = useState([]);
  const [editQuestions, setEditQuestions] = useState([]); // Questions for the position being edited

  useEffect(() => {
    const loadPositions = async () => {
      try {
        const docs = await fetchPositions();
        setPositions(docs);
        setLoadingPositions(false);
        
        const hrExists = docs.some(p => p.title.toLowerCase() === "hr");
        if (!hrExists) seedHRPosition();
      } catch (err) {
        console.error("Positions Load Error:", err);
        setLoadingPositions(false);
      }
    };
    loadPositions();
    const interval = setInterval(loadPositions, 10000);
    return () => clearInterval(interval);
  }, []);

  const seedHRPosition = async () => {
    const hrTitle = "HR";
    const hrDesc = "Focuses on recruitment, employee relations, performance management, and organizational culture. Strategic advisor to leadership on workforce planning.";
    const hrQuestions = [
      "How do you handle conflict between team members?",
      "What is your approach to recruitment and sourcing top talent?",
      "How do you ensure a diverse and inclusive workplace?",
      "Describe your experience with performance management systems.",
      "How do you stay updated with labor laws and regulations?",
      "What strategies do you use for employee retention?",
      "How do you handle sensitive employee information?",
      "Describe a time you had to implement a difficult HR policy?",
      "How do you balance organizing needs with employee needs?",
      "What role does HR play in organizational culture?"
    ];

    try {
      const pos = await createRecord(LARK_TABLES.POSITIONS, {
        "Title": hrTitle,
        "Job Description": hrDesc,
        "Created At": Date.now(),
      });
      for (const qText of hrQuestions) {
        await createRecord(LARK_TABLES.QUESTIONS, {
          "Question": qText,
          "Category": hrTitle,
          "Difficulty": "Medium",
          "Created At": Date.now()
        });
      }
    } catch (e) { console.error("HR Seed Error:", e); }
  };

  const addPosition = async () => {
    if (!newPositionTitle.trim()) return alert("Please enter a position title");
    if (!newJobDescription.trim()) return alert("Please enter a job description");
    
    if (isAddingQuestions && newQuestions.some(q => !q.trim())) {
      return alert("Please fill in all 10 questions");
    }

    try {
      await createRecord(LARK_TABLES.POSITIONS, {
        "Title": newPositionTitle.trim(),
        "Job Description": newJobDescription.trim(),
        "Created At": Date.now(),
      });

      if (isAddingQuestions) {
        for (const qText of newQuestions) {
          await createRecord(LARK_TABLES.QUESTIONS, {
            "Question": qText.trim(),
            "Category": newPositionTitle.trim(),
            "Difficulty": "Medium",
            "Created At": Date.now()
          });
        }
      }

      setNewPositionTitle("");
      setNewJobDescription("");
      setNewQuestions(Array(10).fill(""));
      setIsAddingQuestions(false);
      alert("Position added successfully!");
    } catch (err) {
      console.error(err);
      alert("Failed to add position.");
    }
  };

  const deletePosition = async (id) => {
    if (!window.confirm("Are you sure you want to delete this position?")) return;
    try { await deleteRecord(LARK_TABLES.POSITIONS, id); } catch (err) { console.error(err); alert("Failed to delete."); }
  };

  const startEditPosition = async (pos) => { 
    setEditPositionId(pos.id); 
    setEditPositionTitle(pos.title); 
    setEditJobDescription(pos.jobDescription); 
    
    // Fetch related questions for editing
    try {
        const result = await fetchQuestions();
        let related = result
            .filter(q => (q.Category || "").toLowerCase() === (pos.Title || "").toLowerCase())
            .slice(0, 10);
        
        if (related.length === 0) {
            related = Array(10).fill(null).map((_, i) => ({ id: `new-${i}`, question: "", category: pos.Title, isNew: true }));
        } else if (related.length < 10) {
            const extra = Array(10 - related.length).fill(null).map((_, i) => ({ id: `new-${related.length + i}`, question: "", category: pos.Title, isNew: true }));
            related = [...related, ...extra];
        }

        setEditQuestions(related);
    } catch (e) { 
        console.error("Error fetching edit questions:", e); 
        setEditQuestions(Array(10).fill(null).map((_, i) => ({ id: `new-${i}`, question: "", category: pos.Title, isNew: true })));
    }
  };

  const saveEditPosition = async () => {
    if (!editPositionTitle.trim()) return alert("Please enter a position title");
    if (!editJobDescription.trim()) return alert("Please enter a job description");
    try {
      // 1. Update the position itself
      await updateRecord(LARK_TABLES.POSITIONS, editPositionId, { 
        "Title": editPositionTitle.trim(), 
        "Job Description": editJobDescription.trim() 
      });
      
      const originalPos = positions.find(p => p.id === editPositionId);
      const isTitleChanged = originalPos && originalPos.Title !== editPositionTitle.trim();

      for (const eq of editQuestions) {
          if (!eq.question.trim()) continue; 

          if (eq.isNew) {
              await createRecord(LARK_TABLES.QUESTIONS, {
                  "Question": eq.question.trim(),
                  "Category": editPositionTitle.trim(),
                  "Difficulty": "Medium",
                  "Created At": Date.now()
              });
          } else {
              const updateData = { "Question": eq.question.trim() };
              if (isTitleChanged) updateData["Category"] = editPositionTitle.trim();
              await updateRecord(LARK_TABLES.QUESTIONS, eq.id, updateData);
          }
      }
      
      setEditPositionId(null);
      setEditQuestions([]);
      alert("Position and questions updated successfully!");
    } catch (err) { console.error(err); alert("Failed to update."); }
  };

  useEffect(() => {
    if (!viewingQuestionsFor) return;
    const pos = positions.find(p => p.id === viewingQuestionsFor);
    if (!pos) return;

    const loadRelated = async () => {
        try {
            const allQ = await fetchQuestions();
            setPositionQuestions(allQ.filter(q => (q.Category || "").toLowerCase() === (pos.Title || "").toLowerCase()));
        } catch (e) {}
    };
    loadRelated();
  }, [viewingQuestionsFor, positions]);


  return (
    <>
      <div className="admin-header" style={{ padding: "24px 32px 0", marginBottom: 0 }}>
        <div className="admin-header-left">
          <h2 style={{ fontSize: "24px", margin: "0 0 4px", color: "#1a2332", fontWeight: 800 }}>💼 Position Management</h2>
          <p style={{ color: "#64748b", fontSize: "14px", margin: 0 }}>Add positions with job descriptions for AI rating.</p>
        </div>
        <button className="back-btn" onClick={() => navigate("/dashboard")} style={{ margin: 0 }}>
          ← Back to Dashboard
        </button>
      </div>

      {/* Positions List - NOW AT TOP */}
      {loadingPositions ? (
        <p style={{ textAlign: "center", color: "#94a3b8" }}>Loading positions...</p>
      ) : positions.length === 0 ? (
        <div style={{ textAlign: "center", padding: 30, color: "#94a3b8" }}>
          <span style={{ fontSize: 40 }}>📋</span>
          <p style={{ marginTop: 8 }}>No positions added yet. Add your first position below.</p>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 30 }}>
          {positions.map((pos) => (
            <div key={pos.id} style={{
              background: "white", border: "1px solid #e2e8f0", borderRadius: 10, padding: 16, transition: "box-shadow 0.2s",
            }}
            onMouseOver={(e) => e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.06)"}
            onMouseOut={(e) => e.currentTarget.style.boxShadow = "none"}
            >
              {editPositionId === pos.id ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  <input value={editPositionTitle} onChange={(e) => setEditPositionTitle(e.target.value)} style={{ padding: "8px 12px", borderRadius: 6, border: "1px solid #cbd5e1", fontWeight: 600 }} />
                  <textarea value={editJobDescription} onChange={(e) => setEditJobDescription(e.target.value)} rows={4} style={{ padding: "8px 12px", borderRadius: 6, border: "1px solid #cbd5e1", fontSize: "0.85rem", fontFamily: "Inter, sans-serif", resize: "vertical" }} />
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 12 }}>
                    {editQuestions.map((eq, i) => (
                        <div key={eq.id} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                            <label style={{ fontSize: "0.7rem", color: "#64748b" }}>Q{i+1}</label>
                            <input 
                                value={eq.question} 
                                onChange={(e) => {
                                    const updated = [...editQuestions];
                                    updated[i] = { ...updated[i], question: e.target.value };
                                    setEditQuestions(updated);
                                }} 
                                style={{ padding: "8px 10px", borderRadius: 6, border: "1px solid #cbd5e1", fontSize: "0.8rem" }} 
                            />
                        </div>
                    ))}
                  </div>
                  <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
                    <button onClick={saveEditPosition} style={{ padding: "8px 16px", background: "#10b981", color: "white", border: "none", borderRadius: 6, fontSize: "0.85rem", fontWeight: 600, cursor: "pointer" }}>✓ Save Changes</button>
                    <button onClick={() => { setEditPositionId(null); setEditQuestions([]); }} style={{ padding: "8px 16px", background: "#ef4444", color: "white", border: "none", borderRadius: 6, fontSize: "0.85rem", fontWeight: 600, cursor: "pointer" }}>✕ Cancel</button>
                  </div>
                </div>
              ) : (
                <>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <div style={{ flex: 1 }}>
                    <h4 style={{ margin: 0, fontSize: "1rem", color: "#1e293b", display: "flex", alignItems: "center", gap: 8 }}>💼 {pos.Title}</h4>
                    <p style={{ margin: "8px 0 0", fontSize: "0.82rem", color: "#475569", lineHeight: 1.5, whiteSpace: "pre-wrap", maxHeight: 100, overflow: "auto" }}>{pos["Job Description"]}</p>
                  </div>
                  <div style={{ display: "flex", gap: 6, marginLeft: 12, flexShrink: 0 }}>
                    <button 
                        onClick={() => setViewingQuestionsFor(viewingQuestionsFor === pos.id ? null : pos.id)} 
                        style={{ padding: "5px 10px", background: "#f5f3ff", color: "#6d28d9", border: "1px solid #ddd6fe", borderRadius: 6, fontSize: "0.75rem", fontWeight: 600, cursor: "pointer" }}
                    >
                        {viewingQuestionsFor === pos.id ? "🙈 Hide Qs" : "👁️ View Qs"}
                    </button>
                    <button onClick={() => startEditPosition(pos)} style={{ padding: "5px 10px", background: "#f0f9ff", color: "#0369a1", border: "1px solid #bae6fd", borderRadius: 6, fontSize: "0.75rem", fontWeight: 600, cursor: "pointer" }}>✏️ Edit</button>
                    <button onClick={() => deletePosition(pos.id)} style={{ padding: "5px 10px", background: "#fff1f2", color: "#e11d48", border: "1px solid #fecdd3", borderRadius: 6, fontSize: "0.75rem", fontWeight: 600, cursor: "pointer" }}>🗑️ Delete</button>
                  </div>
                </div>
                
                {viewingQuestionsFor === pos.id && (
                    <div style={{ marginTop: 16, padding: 12, background: "#f8fafc", borderRadius: 8, borderLeft: "3px solid #6366f1" }}>
                        <h5 style={{ margin: "0 0 8px", fontSize: "0.85rem", color: "#334155" }}>Questions for this Role:</h5>
                        {positionQuestions.length === 0 ? (
                            <p style={{ fontSize: "0.8rem", color: "#64748b", margin: 0 }}>No specific questions added for this position yet.</p>
                        ) : (
                            <ul style={{ margin: 0, paddingLeft: 20, fontSize: "0.8rem", color: "#475569" }}>
                                {positionQuestions.map((q, i) => <li key={q.id} style={{ marginBottom: 4 }}>{q.question}</li>)}
                            </ul>
                        )}
                    </div>
                )}
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Add Position Form - NOW AT BOTTOM */}
      <div style={{
        background: "white",
        borderRadius: 12, padding: 24, marginBottom: 40, border: "1px solid #e2e8f0",
        boxShadow: "0 4px 6px -1px rgba(0,0,0,0.05)"
      }}>
        <h3 style={{ margin: "0 0 20px", fontSize: "1.1rem", color: "#1e293b" }}>➕ Add New Position</h3>
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <input
            type="text"
            placeholder="Position Title (e.g. Financial Executive, Software Engineer)"
            value={newPositionTitle}
            onChange={(e) => setNewPositionTitle(e.target.value)}
            style={{ padding: "12px 16px", borderRadius: 8, border: "1px solid #cbd5e1", fontSize: "0.95rem", fontWeight: 500, outline: "none" }}
          />
          <textarea
            placeholder={"Job Description — Describe the role, responsibilities, required skills...\n\nAI will use this to rate resumes and interview answers."}
            value={newJobDescription}
            onChange={(e) => setNewJobDescription(e.target.value)}
            rows={4}
            style={{ padding: "12px 16px", borderRadius: 8, border: "1px solid #cbd5e1", fontSize: "0.9rem", resize: "vertical", fontFamily: "Inter, sans-serif", outline: "none", lineHeight: 1.6 }}
          />

          {isAddingQuestions && (
            <div style={{ marginTop: 8, padding: 16, background: "#f1f5f9", borderRadius: 10, border: "1px solid #e2e8f0" }}>
                <h4 style={{ margin: "0 0 12px", fontSize: "0.9rem", color: "#475569" }}>Add 10 Interview Questions:</h4>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                    {newQuestions.map((q, i) => (
                        <input 
                            key={i}
                            type="text"
                            placeholder={`Question ${i+1}`}
                            value={q}
                            onChange={(e) => {
                                const updated = [...newQuestions];
                                updated[i] = e.target.value;
                                setNewQuestions(updated);
                            }}
                            style={{ padding: "8px 12px", borderRadius: 6, border: "1px solid #cbd5e1", fontSize: "0.85rem" }}
                        />
                    ))}
                </div>
            </div>
          )}

          <div style={{ display: "flex", gap: 10 }}>
            <button
                onClick={addPosition}
                style={{
                padding: "12px 24px", background: "linear-gradient(135deg, #6366f1, #4f46e5)", color: "white",
                border: "none", borderRadius: 8, fontWeight: 600, fontSize: "0.9rem", cursor: "pointer",
                transition: "all 0.2s",
                }}
            >
                {isAddingQuestions ? "💾 Save Position & Questions" : "➕ Add Position Only"}
            </button>
            <button
                onClick={() => setIsAddingQuestions(!isAddingQuestions)}
                style={{
                    padding: "12px 24px", background: isAddingQuestions ? "#f1f5f9" : "#eff6ff", color: isAddingQuestions ? "#475569" : "#2563eb",
                    border: "1px solid " + (isAddingQuestions ? "#e2e8f0" : "#bfdbfe"), borderRadius: 8, fontWeight: 600, fontSize: "0.9rem", cursor: "pointer",
                }}
            >
                {isAddingQuestions ? "✕ Cancel Questions" : "📝 Add with Questions"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

// ─────────────────── Settings Page (Questions Management) ───────────────────
function SettingsPage() {
  const navigate = useNavigate();

  const [questions, setQuestions] = useState([]);
  const [loadingQuestions, setLoadingQuestions] = useState(true);

  const [newQuestion, setNewQuestion] = useState("");
  const [newCategory, setNewCategory] = useState("General");
  const [newDifficulty, setNewDifficulty] = useState("Medium");

  const [editId, setEditId] = useState(null);
  const [editQuestion, setEditQuestion] = useState("");
  const [editCategory, setEditCategory] = useState("");
  const [editDifficulty, setEditDifficulty] = useState("");

  const [filterCategory, setFilterCategory] = useState("All");
  const [filterDifficulty, setFilterDifficulty] = useState("All");
  const [searchText, setSearchText] = useState("");

  const categories = ["General", "Aptitude"];
  const difficulties = ["Easy", "Medium", "Hard"];

  useEffect(() => {
    const loadQuestions = async () => {
      try {
        const allQs = await fetchQuestions();
        setQuestions(allQs.filter(q => ["general", "aptitude"].includes((q.Category || "").toLowerCase())));
        setLoadingQuestions(false);
      } catch (err) {
        console.error("Settings Load Error:", err);
        setLoadingQuestions(false);
      }
    };
    loadQuestions();
  }, []);

  const addQuestion = async () => {
    if (!newQuestion.trim()) return alert("Please enter a question");
    try {
      await createRecord(LARK_TABLES.QUESTIONS, { 
        "Question": newQuestion, 
        "Category": newCategory, 
        "Difficulty": newDifficulty, 
        "Created At": Date.now() 
      });
      setNewQuestion(""); setNewCategory("General"); setNewDifficulty("Medium");
    } catch (err) { console.error(err); alert("Failed to add question."); }
  };

  const deleteQuestion = async (id) => {
    if (!window.confirm("Are you sure you want to delete this question?")) return;
    try { await deleteRecord(LARK_TABLES.QUESTIONS, id); } catch (err) { console.error(err); alert("Failed to delete."); }
  };

  const startEdit = (q) => { setEditId(q.id); setEditQuestion(q.Question); setEditCategory(q.Category); setEditDifficulty(q.Difficulty); };

  const saveEdit = async () => {
    if (!editQuestion.trim()) return alert("Please enter a question");
    try {
      await updateRecord(LARK_TABLES.QUESTIONS, editId, { 
        "Question": editQuestion, 
        "Category": editCategory, 
        "Difficulty": editDifficulty 
      });
      setEditId(null);
    } catch (err) { console.error(err); alert("Failed to update."); }
  };

  const filteredQuestions = questions.filter((q) => {
    const catMatch = filterCategory === "All" || q.Category === filterCategory;
    const diffMatch = filterDifficulty === "All" || q.Difficulty === filterDifficulty;
    const searchMatch = q.Question.toLowerCase().includes(searchText.toLowerCase());
    return catMatch && diffMatch && searchMatch;
  });

  const totalQuestions = questions.length;
  const totalGeneral = questions.filter(q => (q.Category || "").toLowerCase() === "general").length;
  const totalAptitude = questions.filter(q => (q.Category || "").toLowerCase() === "aptitude").length;

  return (
    <>
      <div className="admin-header" style={{ padding: "24px 32px 0", marginBottom: 10 }}>
        <div className="admin-header-left">
          <h2 style={{ fontSize: "24px", margin: "0 0 4px", color: "#1a2332", fontWeight: 800 }}>⚙️ Questions & Settings</h2>
          <p style={{ color: "#64748b", fontSize: "14px", margin: 0 }}>Manage the interview question bank across categories.</p>
        </div>
        <button className="back-btn" onClick={() => navigate("/dashboard")} style={{ margin: 0 }}>
          ← Back to Dashboard
        </button>
      </div>

      {/* Stats Cards */}
      <div className="stats-cards" style={{ display: 'flex', gap: '15px' }}>
        <div className="card stat-card" style={{ flex: 1 }}><h3>Total Questions</h3><p>{totalQuestions}</p></div>
        <div className="card stat-card" style={{ flex: 1 }}><h3>General</h3><p>{totalGeneral}</p></div>
        <div className="card stat-card" style={{ flex: 1 }}><h3>Aptitude</h3><p>{totalAptitude}</p></div>
      </div>

      {/* Add Question */}
      <div className="card add-card">
        <input type="text" placeholder="Enter question" value={newQuestion} onChange={(e) => setNewQuestion(e.target.value)} />
        <select value={newCategory} onChange={(e) => setNewCategory(e.target.value)}>
          {categories.map(c => <option key={c}>{c}</option>)}
        </select>
        <select value={newDifficulty} onChange={(e) => setNewDifficulty(e.target.value)}>
          {difficulties.map(d => <option key={d}>{d}</option>)}
        </select>
        <button onClick={addQuestion}>Add Question</button>
      </div>

      {/* Filters */}
      <div className="filters">
        <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}>
          <option>All</option>
          {categories.map(c => <option key={c}>{c}</option>)}
        </select>
        <select value={filterDifficulty} onChange={(e) => setFilterDifficulty(e.target.value)}>
          <option>All</option>
          {difficulties.map(d => <option key={d}>{d}</option>)}
        </select>
        <input type="text" placeholder="Search questions..." value={searchText} onChange={(e) => setSearchText(e.target.value)} className="search-bar" />
      </div>

      {/* Questions Table */}
      <div className="card table-card">
        {loadingQuestions ? (
          <p>Loading questions...</p>
        ) : filteredQuestions.length === 0 ? (
          <p>No questions found.</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Question</th>
                <th>Category</th>
                <th>Difficulty</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredQuestions.map((q) => (
                <tr key={q.id}>
                  {editId === q.id ? (
                    <>
                      <td><input value={editQuestion} onChange={(e) => setEditQuestion(e.target.value)} /></td>
                      <td>
                        <select value={editCategory} onChange={(e) => setEditCategory(e.target.value)}>
                          {categories.map(c => <option key={c}>{c}</option>)}
                        </select>
                      </td>
                      <td>
                        <select value={editDifficulty} onChange={(e) => setEditDifficulty(e.target.value)}>
                          {difficulties.map(d => <option key={d}>{d}</option>)}
                        </select>
                      </td>
                      <td>
                        <button onClick={saveEdit}>Save</button>
                        <button onClick={() => setEditId(null)}>Cancel</button>
                      </td>
                    </>
                  ) : (
                    <>
                      <td>{q.Question}</td>
                      <td>{q.Category}</td>
                      <td>{q.Difficulty}</td>
                      <td>
                        <button onClick={() => startEdit(q)}>Edit</button>
                        <button onClick={() => deleteQuestion(q.id)}>Delete</button>
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}

// ─────────────────── Main App ───────────────────
function App() {
  const [user, setUser] = useState(null);
  const [role, setRole] = useState(null);
  const [loadingAuth, setLoadingAuth] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        try {
          if (currentUser.email === ADMIN_EMAIL) {
            setRole("admin");
          } else {
            const userDoc = await getDoc(doc(db, "users", currentUser.uid));
            if (userDoc.exists()) {
              setRole(userDoc.data().role || "candidate");
            } else {
              setRole("candidate");
            }
          }
        } catch (err) {
          console.error("Error fetching user role:", err);
          setRole(currentUser.email === ADMIN_EMAIL ? "admin" : "candidate");
        }
        setUser(currentUser);
      } else {
        setUser(null);
        setRole(null);
      }
      setLoadingAuth(false);
    });

    // 🔍 Auto-Discover Tables & Base on Startup
    discoverBase().then(() => {
      discoverTables();
    });

    return () => unsubscribe();
  }, []);

  const handleLogout = async () => {
    const keys = [
      "iv_candidateName", "iv_isFieldSelecting", "iv_selectedField", "iv_questions", 
      "iv_currentIndex", "iv_isInterviewStarted", "iv_resumeData", "iv_isFormSubmitted", 
      "iv_answers", "iv_currentInterviewId", "iv_selectedPosition",
      "hr_personal_details", "hr_bank_details", "hr_id_details", "hr_qualifications", 
      "hr_additional_details", "hr_experience", "hr_health_details"
    ];
    keys.forEach(k => localStorage.removeItem(k));
    
    await signOut(auth);
    setUser(null);
    setRole(null);
  };

  if (loadingAuth) {
    return <div className="interview-loading"><div className="loading-spinner" /><p>Checking authentication...</p></div>;
  }

  if (!user) {
    return <Login onLogin={() => {}} />;
  }

  return (
    <>
      <Routes>
        {role === "admin" ? (
          <>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={<AdminLayout user={user} onLogout={handleLogout}><DashboardPage user={user} /></AdminLayout>} />
            <Route path="/positions" element={<AdminLayout user={user} onLogout={handleLogout}><PositionsPage /></AdminLayout>} />
            <Route path="/settings" element={<AdminLayout user={user} onLogout={handleLogout}><SettingsPage /></AdminLayout>} />
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/interview-detail/:id" element={<InterviewDetail />} />
            <Route path="/interview" element={<CandidateInterview user={user} role="admin" />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </>
        ) : (
          <>
            <Route path="/" element={<Navigate to="/interview" replace />} />
            <Route path="/interview" element={
              <>
                <header className="topbar" style={{ position: "absolute", top: 0, width: "100%", background: "transparent", border: "none", zIndex: 10 }}>
                  <div style={{ marginLeft: "auto", padding: "16px 32px" }}>
                    <div className="user-info" style={{background: "#fff", display: "inline-flex", boxShadow: "0 2px 8px rgba(0,0,0,0.1)"}}>
                      <span>{user.email}</span>
                      <button className="logout-btn" onClick={handleLogout}>Logout</button>
                    </div>
                  </div>
                </header>
                <CandidateInterview user={user} />
              </>
            } />
            <Route path="*" element={<Navigate to="/interview" replace />} />
          </>
        )}
      </Routes>
    </>
  );
}

export default App;